import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Mail,
  Phone,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { Partner } from "@/lib/api";
import { useRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";

/**
 * Two-step password reset UI for the Partner Portal.
 *
 * 1) /partner/forgot-password  — partner enters email + mobile, we email a link
 * 2) /partner/reset-password?token=... — partner picks a new password
 */

const Shell = ({ testId, eyebrow, title, children }) => (
  <div
    data-testid={testId}
    className="min-h-screen bg-[#04102B] text-white flex items-center justify-center px-5 py-10"
  >
    <div className="w-full max-w-md">
      <Link to="/" className="block text-center mb-8">
        <div className="font-display text-3xl tracking-tight">SAHYOG FINANCE</div>
        <div className="text-[#D4AF37] text-xs tracking-[0.22em] uppercase mt-1">
          {eyebrow}
        </div>
      </Link>
      <div className="bg-[#0A1F44] rounded-3xl p-7 border border-[#D4AF37]/30">
        <h1 className="font-display text-2xl">{title}</h1>
        {children}
      </div>
      <div className="mt-5 text-center">
        <Link
          to="/partner/login"
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#F0D788]"
        >
          <ArrowLeft size={12} /> Back to sign in
        </Link>
      </div>
    </div>
  </div>
);

const RecaptchaNotice = () =>
  RECAPTCHA_ENABLED ? (
    <p className="mt-4 text-[11px] text-white/40 flex items-start gap-1.5 leading-snug">
      <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
      <span>
        Protected by reCAPTCHA — the Google{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-[#F0D788]"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-[#F0D788]"
        >
          Terms of Service
        </a>{" "}
        apply.
      </span>
    </p>
  ) : null;

/* ─── STEP 1 · Forgot password ────────────────────────────────────── */

export function PartnerForgotPassword() {
  const getRecaptchaToken = useRecaptchaToken();
  const [form, setForm] = useState({ email: "", phone: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.phone.trim()) {
      setStatus({ state: "error", message: "Please enter both your email and mobile number." });
      return;
    }
    setStatus({ state: "loading", message: "Sending reset link…" });
    try {
      const recaptcha_token = await getRecaptchaToken("partner-forgot-password");
      // Backend soft-fails on missing/expired tokens so we no longer block
      // real customers whose browser blocked grecaptcha or was slow to load.
      const r = await Partner.forgotPassword({
        email: form.email.trim(),
        phone: form.phone.trim(),
        recaptcha_token,
      });
      setStatus({ state: "success", message: r?.message || "Check your inbox." });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not send the reset email. Please try again in a moment.",
      });
    }
  };

  return (
    <Shell testId="partner-forgot-password-page" eyebrow="Partner Portal · Reset" title="Forgot your password?">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Enter the <strong className="text-white/85">email address</strong> and{" "}
        <strong className="text-white/85">mobile number</strong> registered on your Sahyog
        partner account. If both match, we&apos;ll email you a secure link that expires in 15
        minutes.
      </p>

      <form data-testid="partner-forgot-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          Email address <span className="text-[#D4AF37]">*</span>
        </label>
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="partner-forgot-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="you@example.com"
            className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
          />
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          Mobile number <span className="text-[#D4AF37]">*</span>
        </label>
        <div className="relative">
          <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="partner-forgot-phone"
            type="tel"
            required
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="10-digit mobile"
            className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
          />
        </div>

        {status.state === "error" && (
          <div
            data-testid="partner-forgot-error"
            className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div
            data-testid="partner-forgot-success"
            className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm leading-snug"
          >
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="partner-forgot-submit"
          type="submit"
          disabled={status.state === "loading"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} />
          {status.state === "loading" ? "Sending…" : "Send reset link"}
        </button>

        <RecaptchaNotice />
      </form>

      <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-white/45 leading-relaxed">
        Registered without an email address, or don&apos;t remember either detail? Contact us at{" "}
        <a href="tel:+917016975324" className="text-[#F0D788] hover:underline">
          70169 75324
        </a>{" "}
        or{" "}
        <a href="mailto:sahyogpfir@gmail.com" className="text-[#F0D788] hover:underline">
          sahyogpfir@gmail.com
        </a>{" "}
        for a manual reset.
      </div>
    </Shell>
  );
}

/* ─── STEP 2 · Reset password (from email link) ───────────────────── */

export function PartnerResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const getRecaptchaToken = useRecaptchaToken();
  const [form, setForm] = useState({ pw: "", pw2: "" });
  const [status, setStatus] = useState({ state: token ? "idle" : "no-token", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.pw.length < 6) {
      setStatus({ state: "error", message: "Password must be at least 6 characters." });
      return;
    }
    if (form.pw !== form.pw2) {
      setStatus({ state: "error", message: "Passwords do not match." });
      return;
    }
    setStatus({ state: "loading", message: "Updating password…" });
    try {
      const recaptcha_token = await getRecaptchaToken("partner-reset-password");
      await Partner.resetPassword({ token, new_password: form.pw, recaptcha_token });
      setStatus({ state: "success", message: "Password updated. Redirecting to sign-in…" });
      setTimeout(() => navigate("/partner/login", { replace: true }), 2000);
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not reset the password. The link may have expired — please request a new one.",
      });
    }
  };

  if (status.state === "no-token") {
    return (
      <Shell testId="partner-reset-password-page" eyebrow="Partner Portal · Reset" title="Reset link missing">
        <p className="text-white/60 text-sm mt-3 leading-relaxed">
          The reset link is missing its security token. Please open the link exactly as it was
          emailed to you, or request a new one.
        </p>
        <Link
          to="/partner/forgot-password"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold"
        >
          Request a new link
        </Link>
      </Shell>
    );
  }

  return (
    <Shell testId="partner-reset-password-page" eyebrow="Partner Portal · Reset" title="Set a new password">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Pick a strong password (min 6 characters). You&apos;ll sign in with your new password
        immediately after.
      </p>

      <form data-testid="partner-reset-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          New password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="partner-reset-new-pw"
          type="password"
          required
          minLength={6}
          value={form.pw}
          onChange={(e) => setForm((f) => ({ ...f, pw: e.target.value }))}
          placeholder="At least 6 characters"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          Confirm new password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="partner-reset-new-pw2"
          type="password"
          required
          minLength={6}
          value={form.pw2}
          onChange={(e) => setForm((f) => ({ ...f, pw2: e.target.value }))}
          placeholder="Type the same password again"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        {status.state === "error" && (
          <div
            data-testid="partner-reset-error"
            className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div
            data-testid="partner-reset-success"
            className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm"
          >
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="partner-reset-submit"
          type="submit"
          disabled={status.state === "loading" || status.state === "success"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} />
          {status.state === "loading" ? "Updating…" : "Update password"}
        </button>

        <RecaptchaNotice />
      </form>
    </Shell>
  );
}
